import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CreatePatientFromReferralPayload,
  CreatePatientFromReferralResponse,
  CreateReferrerFromReferralPayload,
  FilterByQuery,
  IncludeQuery,
  LinkableTrajectory,
  LinkReferralToPatientPayload,
  LinkTreatmentPlanToReferralPayload,
  LinkTreatmentPlanToReferralResponse,
  PaginationQuery,
  Referral,
  ReferralCounts,
  ReferralPatientMatch,
  Response,
  SelectableReferrer,
  SortByQuery,
  UpdateReferralStatusPayload,
} from "~/types/api";

export function createReferralApi({ getRequest, postRequest, patchRequest, getPaginationRequest }: ApiRequestHelpers) {
  /** Fetches a paginated list of referrals. */
  const getReferrals = (
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<Referral>("/referrals", pagination, sortBy, includes, filterBy);
  };

  /** Fetches a single referral by ID. */
  const getReferral = (referralId: string) => getRequest<Response<Referral>>(`/referrals/${referralId}`);

  const downloadReferralAttachment = (referralId: string, attachmentId: string) =>
    getRequest<Blob>(`/referrals/${referralId}/attachments/${attachmentId}`, { responseType: "blob" });

  const getNewReferralCount = () => getRequest<Response<ReferralCounts>>("/referrals/count");

  /** Creates a new patient from a referral's data. */
  const createPatientFromReferral = (referralId: string, data: CreatePatientFromReferralPayload) =>
    postRequest<CreatePatientFromReferralResponse>(`/referrals/${referralId}/create-patient`, data, {
      suppressValidationToast: true,
    });

  /** Creates a new treatment from a referral's data. */
  const createTreatmentFromReferral = (referralId: string, data: { appointment_id: number }) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/create-treatment`, data);

  /** Links a referral to an existing patient. */
  const linkReferralToPatient = (referralId: string, data: LinkReferralToPatientPayload) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/link-patient`, data, {
      suppressValidationToast: true,
    });

  /** Links an appointment to a referral. */
  const linkAppointmentToReferral = (referralId: string, data: { appointment_id: number }) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/link-appointment`, data);

  /** Links a treatment plan to a referral. */
  const linkTreatmentPlanToReferral = (referralId: string, data: LinkTreatmentPlanToReferralPayload) =>
    postRequest<LinkTreatmentPlanToReferralResponse>(`/referrals/${referralId}/link-treatment`, data, {
      suppressValidationToast: true,
    });

  const linkableTrajectoriesForReferral = (referralId: string) =>
    getRequest<Response<LinkableTrajectory[]>>(`/referrals/${referralId}/linkable-trajectories`);

  const referrerCandidates = (referralId: string) =>
    getRequest<Response<SelectableReferrer[]>>(`/referrals/${referralId}/referrer-candidates`);

  const linkReferrerToReferral = (referralId: string, data: { referrer_id: string }) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/link-referrer`, data);

  const createReferrerFromReferral = (referralId: string, data: CreateReferrerFromReferralPayload) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/create-referrer`, data);

  /** Updates the status of a referral. */
  const updateReferralStatus = (referralId: string, data: UpdateReferralStatusPayload) =>
    patchRequest<Response<Referral>>(`/referrals/${referralId}/status`, data);

  /** Finds existing patients that potentially match a referral. */
  const findPotentialPatientMatches = (referralId: string) =>
    getRequest<Response<ReferralPatientMatch[]>>(`/referrals/${referralId}/potential-matches`);

  const autoLinkReferralToPatient = (referralId: string) =>
    postRequest<Response<Referral>>(`/referrals/${referralId}/auto-link-patient`);

  return {
    getReferrals,
    getReferral,
    downloadReferralAttachment,
    getNewReferralCount,
    createPatientFromReferral,
    createTreatmentFromReferral,
    linkReferralToPatient,
    linkAppointmentToReferral,
    linkTreatmentPlanToReferral,
    linkableTrajectoriesForReferral,
    referrerCandidates,
    linkReferrerToReferral,
    createReferrerFromReferral,
    updateReferralStatus,
    findPotentialPatientMatches,
    autoLinkReferralToPatient,
  };
}

export type ReferralApi = ReturnType<typeof createReferralApi>;
